import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import LanguageSelector from './LanguageSelector';
import { handleBlockedClick } from '../../config/blockedRoutes';
import { notificationService } from '../../api/services';
import { useAuth } from '../../contexts/AuthContext';

export default function Navbar() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileServiceOpen, setMobileServiceOpen] = useState(false);
  const [navHidden, setNavHidden] = useState(false);
  const mobileServiceRef = useRef(null);
  const lastScrollY = useRef(0);

  const isLoggedIn = !!localStorage.getItem('accessToken');
  const [unreadCount, setUnreadCount] = useState(0);
  // 최근 polling 시 받은 sent 알림 ID 목록 — 클릭 시 비동기 fetch 없이 즉시 dismissed 에 저장 가능.
  const sentIdsRef = useRef([]);

  // 사용자별 dismissed 알림 ID 보관 (백엔드가 read state를 다루지 않아 클라이언트가 추적)
  const dismissedKey = `notif_dismissed_${localStorage.getItem('userEmail') || 'anonymous'}`;
  const getDismissed = () => {
    try { return new Set(JSON.parse(localStorage.getItem(dismissedKey) || '[]')); }
    catch { return new Set(); }
  };
  const saveDismissed = (set) => {
    try { localStorage.setItem(dismissedKey, JSON.stringify([...set])); } catch {}
  };

  // 로그인 상태일 때 unread 알림 카운트 로드 (60초마다 polling)
  useEffect(() => {
    if (!isLoggedIn) { setUnreadCount(0); return; }
    let cancelled = false;
    const load = () => {
      notificationService.getList().then((data) => {
        if (cancelled) return;
        const items = Array.isArray(data) ? data : (data?.items || []);
        const sent = items.filter((n) => n.status === 'sent');
        sentIdsRef.current = sent.map((n) => n.id);
        const dismissed = getDismissed();
        const unread = sent.filter((n) => !dismissed.has(n.id));
        setUnreadCount(unread.length);
      }).catch(() => {});
    };
    load();
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const mainNav = [
    { label: t('nav.private_transfer'), to: '/transfer', icon: 'van' },
    { label: t('nav.private_tour'), to: '/tour', icon: 'place' },
    { label: t('nav.private_shopping'), to: '/shopping', icon: 'shopping' },
  ];

  const utilNav = [
    { key: 'reservation', label: t('nav.reservation_lookup'), to: '/reservation' },
    { key: 'inquiry', label: t('nav.inquiry'), to: '/inquiry' },
    { key: 'faq', label: t('nav.customer_center'), to: '/faq' },
    { key: 'partnership', label: t('nav.partnership'), to: '/partnership' },
  ];

  // Determine active service for mobile indicator
  const activeService = mainNav.find((item) => pathname.startsWith(item.to)) || null;

  useEffect(() => {
    function handleClick(e) {
      if (mobileServiceRef.current && !mobileServiceRef.current.contains(e.target)) {
        setMobileServiceOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  /* Mobile: hide navbar on scroll down, show on scroll up — 메뉴 열린 상태에서는 숨기지 않음 */
  useEffect(() => {
    const handleScroll = () => {
      if (mobileMenuOpen) return;
      const currentY = window.scrollY;
      const delta = currentY - lastScrollY.current;
      if (delta > 5 && currentY > 60) {
        setNavHidden(true);
      } else if (delta < -5) {
        setNavHidden(false);
      }
      lastScrollY.current = currentY;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [mobileMenuOpen]);

  const isActive = (to) => pathname.startsWith(to);

  const handleLogout = async () => {
    // AuthContext logout() 으로 user state + localStorage 일괄 정리.
    // 이전엔 localStorage 만 지워서 user state 가 stale 로 남아, 다시 [로그인] 클릭 시
    // LoginPage useEffect 가 user 있음으로 판단해 홈으로 리다이렉트하던 회귀.
    await logout();
    navigate('/login');
  };

  return (
    <nav className={`bg-white border-b border-mono-100 sticky top-0 z-[60] pt-safe transition-transform duration-300 ${navHidden ? '-translate-y-full sm:translate-y-0' : ''}`}>
      <div className="max-w-container mx-auto px-2.5 sm:px-5 flex lg:grid lg:grid-cols-[1fr_auto_1fr] items-center justify-between gap-2 sm:gap-4 lg:gap-0 h-navbar">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <img src="/images/logo/logo.png" alt="VIP VAN TOUR - Seoul Private Tour" className="h-8" />
        </Link>

        {/* Desktop Main Nav - centered via grid */}
        <div className="navbar-main hidden lg:flex items-center justify-center gap-8">
          {mainNav.map((item) => {
            const active = isActive(item.to);
            return (
              <Link
                key={item.to}
                to={item.to === '/transfer' ? '/transfer/airport' : item.to}
                onClick={(e) => handleBlockedClick(e, item.to)}
                className={`navbar-main-link flex items-center gap-1.5 text-base font-bold transition whitespace-nowrap ${
                  active ? 'text-mono-900' : 'text-mono-500 hover:text-mono-900'
                }`}
              >
                {active && <Icon name={item.icon} size={20} />}
                {item.label}
              </Link>
            );
          })}
        </div>

        {/* Desktop Right - Utility Links + Language + Login/User */}
        <div className="navbar-right hidden lg:flex items-center gap-2 xl:gap-4 justify-self-end whitespace-nowrap">
          {/* utilNav: mainNav 와 동일하게 lg 이상부터 표시 (#12 — 1024-1280px 구간 노출 누락 수정) */}
          <div className="navbar-utility hidden lg:flex items-center gap-2 xl:gap-4">
            {utilNav.map((item) => {
              const to = item.key === 'reservation' && !isLoggedIn ? '/reservation/guest' : item.to;
              return (
                <Link
                  key={item.key}
                  to={to}
                  onClick={(e) => handleBlockedClick(e, to)}
                  className="navbar-util-link text-xs font-medium text-mono-600 hover:text-mono-900 transition whitespace-nowrap"
                >
                  {item.label}
                </Link>
              );
            })}
          </div>

          <LanguageSelector />

          {isLoggedIn ? (
            <div className="flex items-center gap-2">
              <Link
                to="/inquiry"
                onClick={() => {
                  // BugHerd: 페이지 이동(Link navigate) 이 진행중인 fetch 를 취소시켜 dismissed 가
                  //          localStorage 에 저장 안 되던 버그. 폴링이 캐시한 sentIdsRef 로 즉시
                  //          동기 저장 (비동기 호출 제거).
                  const dismissed = getDismissed();
                  sentIdsRef.current.forEach((id) => dismissed.add(id));
                  saveDismissed(dismissed);
                  setUnreadCount(0);
                }}
                className="relative w-8 h-8 flex items-center justify-center rounded-full hover:bg-mono-50 transition"
              >
                <Icon name="notification" size={20} className="text-mono-500" />
                {unreadCount > 0 && (
                  <span className="absolute top-0 right-0 min-w-[16px] h-[16px] px-1 bg-orange-500 text-white text-2xs font-bold rounded-full flex items-center justify-center leading-none">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </Link>
              <Link to="/mypage/account" className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-mono-50 transition">
                <Icon name="person" size={20} className="text-mono-500" />
              </Link>
            </div>
          ) : (
            <Link
              to="/login"
              className="h-8 px-4 bg-orange-50 text-orange-500 text-xs font-medium rounded-full flex items-center hover:bg-orange-100 transition"
            >
              {t('common.login')}
            </Link>
          )}
        </div>

        {/* Mobile: Active Service Indicator + Language + Hamburger
            순서: [Service] [Lang] [Hamburger] — 언어 드롭다운을 햄버거 바로 옆으로. */}
        <div className="lg:hidden flex items-center gap-1.5 sm:gap-3">
          {/* Active service dropdown trigger — 메뉴 열리면 숨김 */}
          <div className={`relative ${mobileMenuOpen ? 'hidden' : ''}`} ref={mobileServiceRef}>
            <button
              onClick={() => setMobileServiceOpen(!mobileServiceOpen)}
              className="flex items-center gap-1 text-sm font-bold text-mono-900"
            >
              {activeService ? activeService.label : t('nav.private_transfer')}
              <Icon name="chevron-down" size={14} className={`text-mono-600 transition-transform ${mobileServiceOpen ? 'rotate-180' : ''}`} />
            </button>
            {mobileServiceOpen && (
              <div className="absolute top-full right-0 mt-2 w-[160px] bg-white border border-mono-200 rounded-xl shadow-lg py-1 z-50">
                {mainNav.map((item) => {
                  const active = isActive(item.to);
                  return (
                    <Link
                      key={item.to}
                      to={item.to === '/transfer' ? '/transfer/airport' : item.to}
                      onClick={(e) => { if (!handleBlockedClick(e, item.to)) setMobileServiceOpen(false); }}
                      className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm transition ${
                        active ? 'text-orange-500 font-bold bg-orange-50' : 'text-mono-900 hover:bg-mono-50'
                      }`}
                    >
                      <Icon name={item.icon} size={16} />
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          {/* Language selector — 햄버거 왼쪽. 메뉴 열려도 항상 표시. */}
          <LanguageSelector />

          {/* Hamburger */}
          <button className="p-1" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            <Icon name={mobileMenuOpen ? 'close' : 'menu'} size={24} className="text-mono-900" />
          </button>
        </div>
      </div>

      {/* Mobile Full-screen Menu Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-[59px] z-[55] bg-white flex flex-col">
          {/* Body lock + fade-in animation */}
          <style>{`
            body { overflow: hidden; }
            @keyframes fadeInMenu { from { opacity: 0; } to { opacity: 1; } }
          `}</style>
          <div className="flex-1 flex flex-col overflow-y-auto px-6 pt-6" style={{ animation: 'fadeInMenu 0.15s ease-out' }}>
            {/* Main nav */}
            <div className="space-y-1">
              {mainNav.map((item) => {
                const active = isActive(item.to);
                return (
                  <Link
                    key={item.to}
                    to={item.to === '/transfer' ? '/transfer/airport' : item.to}
                    onClick={(e) => { if (!handleBlockedClick(e, item.to)) setMobileMenuOpen(false); }}
                    className={`flex items-center justify-between py-3.5 text-lg font-bold transition-colors ${
                      active ? 'text-orange-500' : 'text-mono-900 active:text-orange-500'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon name={item.icon} size={22} className={active ? 'text-orange-500' : 'text-mono-400'} />
                      {item.label}
                    </div>
                    <Icon name="chevron-right" size={18} className="text-mono-300" />
                  </Link>
                );
              })}
            </div>

            {/* Divider */}
            <div className="my-4 border-t border-mono-100" />

            {/* Utility nav */}
            <div className="space-y-1">
              {utilNav.map((item) => {
                const to = item.key === 'reservation' && !isLoggedIn ? '/reservation/guest' : item.to;
                return (
                  <Link
                    key={item.key}
                    to={to}
                    onClick={(e) => { if (!handleBlockedClick(e, to)) setMobileMenuOpen(false); }}
                    className="flex items-center justify-between py-3 text-base font-medium text-mono-600 active:text-mono-900 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {item.label}
                    </div>
                    <Icon name="chevron-right" size={18} className="text-mono-300" />
                  </Link>
                );
              })}
            </div>

          </div>

          {/* Bottom: 소셜 아이콘 */}
          <div className="shrink-0 px-4 py-4 bg-mono-800 flex items-center justify-center gap-4">
            {[
              { icon: 'kakao', href: 'https://pf.kakao.com/_JScZX/friend', label: 'KakaoTalk' },
              { icon: 'whatsapp', href: 'https://wa.me/821022500365', label: 'WhatsApp' },
              { icon: 'line', href: 'https://line.me/R/ti/p/@690ljwok', label: 'Line' },
              { icon: 'linkedin', href: 'https://www.linkedin.com/company/vipvantour/about/', label: 'LinkedIn' },
              { icon: 'instagram', href: 'https://www.instagram.com/vipvantour.korea/', label: 'Instagram' },
              { icon: 'facebook', href: 'https://www.facebook.com/profile.php?id=61573357626146', label: 'Facebook' },
            ].map((s) => (
              <a key={s.icon} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label} className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center transition hover:bg-white/30">
                <Icon name={s.icon} size={20} className="text-white/80" />
              </a>
            ))}
          </div>

          {/* Bottom: Login/Mypage */}
          <div className="shrink-0 px-4 py-4 border-t border-mono-100" style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}>
            {isLoggedIn ? (
              <div className="flex gap-3">
                <Link
                  to="/mypage/account"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex-1 h-12 bg-orange-500 text-white text-sm font-bold rounded-xl flex items-center justify-center active:bg-orange-600 transition"
                >
                  {t('nav.mypage')}
                </Link>
                <button
                  onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                  className="h-12 px-5 border border-mono-200 text-mono-600 text-sm font-medium rounded-xl flex items-center justify-center active:bg-mono-50 transition"
                >
                  {t('common.logout')}
                </button>
              </div>
            ) : (
              <button
                onClick={() => { setMobileMenuOpen(false); navigate('/login'); }}
                className="w-full h-12 bg-orange-500 text-white text-sm font-bold rounded-xl flex items-center justify-center active:bg-orange-600 transition"
              >
                {t('common.login')}
              </button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
